import {
  ClassificationLevel,
  type MeiaCatalogThreatTargetSystemSelectDto,
  MopAggregation,
  ScoringRole,
  StudyTaskEffort,
  type StudyTaskMoeSelectDto,
  type StudyTaskMopSelectDto,
  type StudyTaskMosSelectDto,
  type StudyTaskSelectDto,
} from '@@types/api.types';
import type { MeiaTaskFormValues } from '@schemas/meia.schema';

type BuildMeiaTaskModalResetKeyParams = {
  existingTaskMoeIds: string[];
  existingTaskMopIds: string[];
  existingTaskMosIds: string[];
  isOpen: boolean;
  swimlaneId: string;
  taskId: string | null;
  taskSnapshotKey?: string;
};

type BuildMeiaTaskFormDefaultsParams = {
  task?: StudyTaskSelectDto;
  taskMoes: StudyTaskMoeSelectDto[];
  taskMops: StudyTaskMopSelectDto[];
  taskMoses: StudyTaskMosSelectDto[];
};

const TASK_POSITION_GAP = 1000;

export const getThreatTargetSystemLibraryOptions = (
  threatTargetSystems: MeiaCatalogThreatTargetSystemSelectDto[],
): { description: string; label: string; value: string }[] =>
  threatTargetSystems
    .map((system) => ({
      description: [system.nation, system.domain, system.classification].join(' · '),
      label: system.name,
      value: system.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

export const buildMeiaTaskFormDefaults = (params: BuildMeiaTaskFormDefaultsParams): MeiaTaskFormValues => {
  const { task, taskMoes, taskMops, taskMoses } = params;

  return {
    classification: task?.classification ?? ClassificationLevel.UNCLASSIFIED,
    description: task?.description ?? '',
    effort: task?.effort ?? StudyTaskEffort.M,
    expectedThreatTargetSystemCode: task?.expectedThreatTargetSystemCode ?? null,
    moeIds: taskMoes.map((entry) => entry.studyMoeId),
    mopAggregation: task?.mopAggregation ?? MopAggregation.WORST,
    mopIds: taskMops.map((entry) => entry.studyMopId),
    mosIds: taskMoses.map((entry) => entry.studyMosId),
    owner: task?.owner ?? '',
    scoringRole: task?.scoringRole ?? ScoringRole.CONTRIBUTING,
    taskName: task?.taskName ?? '',
  };
};

export const buildMeiaTaskModalResetKey = (params: BuildMeiaTaskModalResetKeyParams): string => {
  const { existingTaskMoeIds, existingTaskMopIds, existingTaskMosIds, isOpen, swimlaneId, taskId, taskSnapshotKey } =
    params;

  return JSON.stringify({
    existingTaskMoeIds: [...existingTaskMoeIds].sort(),
    existingTaskMopIds: [...existingTaskMopIds].sort(),
    existingTaskMosIds: [...existingTaskMosIds].sort(),
    isOpen,
    swimlaneId,
    taskId,
    taskSnapshotKey,
  });
};

export const getNextMeiaTaskPosition = (positions: number[]): number => {
  if (positions.length === 0) return TASK_POSITION_GAP;

  return Math.max(...positions) + TASK_POSITION_GAP;
};
